import logger from './logger';

/**
 * Tarih Yardımcı Fonksiyonları
 * 
 * Uygulama genelinde tarih biçimlendirme ve işlemleri için yardımcı işlevler.
 */

/**
 * Bir tarihi okunabilir bir formata dönüştürür
 * 
 * @param {string|Date} dateString - Formatlanacak tarih nesnesi veya string
 * @param {Object} options - Biçimlendirme seçenekleri
 * @returns {string} Formatlanmış tarih string'i
 */
export const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'Belirtilmemiş';

    try {
        const date = new Date(dateString);

        // Geçersiz tarih kontrolü
        if (isNaN(date.getTime())) {
            return 'Geçersiz Tarih';
        }

        // Varsayılan biçimlendirme seçenekleri
        const defaultOptions = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        };

        // Özel seçenekleri varsayılanlarla birleştir
        const formatOptions = { ...defaultOptions, ...options };

        return new Intl.DateTimeFormat('tr-TR', formatOptions).format(date);
    } catch (error) {
        logger.error('Tarih biçimlendirme hatası:', error);
        return 'Tarih hatası';
    }
};

/**
 * Saniye cinsinden süreyi okunabilir bir formata dönüştürür
 * 
 * @param {number} seconds - Formatlanacak saniye sayısı
 * @returns {string} Formatlanmış süre (HH:MM:SS)
 */
export const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '00:00:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const formattedHours = hrs.toString().padStart(2, '0');
    const formattedMins = mins.toString().padStart(2, '0');
    const formattedSecs = secs.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMins}:${formattedSecs}`;
};

/**
 * Saniyeyi HH:MM:SS / MM:SS saat formatına dönüştürür (saat 0 ise atlanır)
 * @param {number} seconds
 * @returns {string}
 */
export const formatDurationClock = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return [
        hours > 0 ? String(hours).padStart(2, '0') : null,
        String(minutes).padStart(2, '0'),
        String(remainingSeconds).padStart(2, '0')
    ].filter(Boolean).join(':');
};

/**
 * Saniyeyi "X dk Y sn" formatına dönüştürür
 * @param {number} seconds
 * @returns {string}
 */
export const formatDurationMinSec = (seconds) => {
    if (seconds == null) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m} dk ${s < 10 ? '0' : ''}${s} sn`;
};

/**
 * Başlangıç/bitiş zaman damgası aralığını "Xs Ydk" veya "Ydk" formatında döndürür
 * @param {string|Date} startTime
 * @param {string|Date} [endTime] - belirtilmezse şimdiki zaman kullanılır
 * @returns {string}
 */
export const formatDurationRange = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    if (diffHours > 0) {
        return `${diffHours}s ${remainingMins}dk`;
    }
    return `${diffMins}dk`;
};

/**
 * Dakikayı "X dakika" / "X saat Y dakika" formatına dönüştürür
 * @param {number} minutes
 * @returns {string}
 */
export const formatMinutesLong = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} dakika`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours} saat ${remainingMinutes} dakika` : `${hours} saat`;
};

